import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  Grid,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PageLayout from '../../components/layout/PageLayout';
import {
  useGetServiceByIdQuery,
  useCreateServiceMutation,
  useUpdateServiceMutation,
  useLazyGetStatusesByModelQuery,
  useLazyGetStatusesQuery,
} from '../../store/api';
import { extractList } from '../../utils/response';
import { useLazySelectOptions } from '../../hooks/useLazySelectOptions';
import { useServerValidationErrors } from '../../hooks/useServerValidationErrors';
import { toast } from 'react-toastify';
import { buildServiceRelationParams } from './serviceApiHelpers';

const buildFormData = (values) => {
  const fd = new FormData();
  fd.append('status_id', values.status_id);
  fd.append('name', values.name ?? '');
  fd.append('internal_charges', String(values.internal_charges ?? ''));
  fd.append('external_charges', String(values.external_charges ?? ''));
  fd.append('description', values.description ?? '');
  return fd;
};

const statusLabel = (s) => s.label ?? s.name ?? s.title ?? String(s.id);

const CreateService = () => {
  const { id: editId } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(editId);

  const [selectedStatus, setSelectedStatus] = useState(null);
  const [name, setName] = useState('');
  const [internalCharges, setInternalCharges] = useState('');
  const [externalCharges, setExternalCharges] = useState('');
  const [description, setDescription] = useState('');
  const [hydrated, setHydrated] = useState(false);
  const { setErrors, applyApiError, clearError, fieldProps } =
    useServerValidationErrors();

  const [fetchStatusesByModel] = useLazyGetStatusesByModelQuery();
  const [fetchStatuses] = useLazyGetStatusesQuery();

  const serviceStatusTrigger = useCallback(
    () => ({
      unwrap: async () => {
        try {
          return await fetchStatusesByModel('service').unwrap();
        } catch {
          return await fetchStatuses({ paginate: 0 }).unwrap();
        }
      },
    }),
    [fetchStatusesByModel, fetchStatuses]
  );

  const statusSelect = useLazySelectOptions(serviceStatusTrigger, {
    mapResult: (res) => {
      const list = extractList(res);
      const arr = Array.isArray(list) ? list : [];
      return arr.map((s) => ({
        id: s.id,
        label: statusLabel(s),
        raw: s,
      }));
    },
  });

  const loadStatuses = useCallback(
    () =>
      statusSelect.load().catch(() => {
        toast.error('Could not load statuses');
      }),
    [statusSelect]
  );

  const {
    data: serviceData,
    isLoading: serviceLoading,
    isError: serviceError,
    error: serviceErr,
  } = useGetServiceByIdQuery(
    { id: editId, params: buildServiceRelationParams() },
    { skip: !isEdit || !editId }
  );
  const [createService, { isLoading: creating }] = useCreateServiceMutation();
  const [updateService, { isLoading: updating }] = useUpdateServiceMutation();
  const submitting = creating || updating;
  const loadingPage = isEdit && (serviceLoading || !hydrated);

  useEffect(() => {
    if (serviceError) {
      toast.error(serviceErr?.data?.message || 'Failed to load service');
      navigate('/services');
    }
  }, [serviceError, serviceErr, navigate]);

  useEffect(() => {
    if (!isEdit || !serviceData || hydrated) return;
    const s = serviceData?.data ?? serviceData;
    if (!s) return;

    setName(s.name ?? '');
    setInternalCharges(s.internal_charges != null ? String(s.internal_charges) : '');
    setExternalCharges(s.external_charges != null ? String(s.external_charges) : '');
    setDescription(s.description ?? '');

    const nestedStatus = s.status && typeof s.status === 'object' ? s.status : null;
    const sid = s.status_id ?? nestedStatus?.id ?? undefined;

    if (sid) {
      if (nestedStatus) {
        const opt = { id: sid, label: statusLabel(nestedStatus), raw: nestedStatus };
        setSelectedStatus(opt);
        statusSelect.setOptions((prev) => (prev.some((o) => o.id === sid) ? prev : [...prev, opt]));
      } else {
        const opt = statusSelect.options.find((o) => o.id === sid);
        setSelectedStatus(opt ?? { id: sid, label: String(sid), raw: {} });
      }
    } else {
      setSelectedStatus(null);
    }
    setHydrated(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional dependency list
  }, [isEdit, serviceData, statusSelect.options, hydrated]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const clientErrors = {};
    if (!name.trim()) clientErrors.name = 'Name is required';
    if (!selectedStatus?.id) clientErrors.status_id = 'Status is required';
    if (internalCharges === '' || Number.isNaN(Number(internalCharges)))
      clientErrors.internal_charges = 'Enter valid internal charges';
    if (externalCharges === '' || Number.isNaN(Number(externalCharges)))
      clientErrors.external_charges = 'Enter valid external charges';
    setErrors(clientErrors);
    if (Object.keys(clientErrors).length > 0) return;

    const values = {
      status_id: selectedStatus.id,
      name: name.trim(),
      internal_charges: internalCharges,
      external_charges: externalCharges,
      description: description.trim(),
    };
    const fd = buildFormData(values);

    try {
      // No relations on write requests — the page navigates away from the response
      if (isEdit) {
        await updateService({ id: editId, data: fd }).unwrap();
        toast.success('Service updated');
      } else {
        await createService(fd).unwrap();
        toast.success('Service added');
      }
      navigate('/services');
    } catch (err) {
      applyApiError(err, isEdit ? 'Failed to update service' : 'Failed to add service');
    }
  };

  if (loadingPage) {
    return (
      <PageLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={320}>
          <CircularProgress />
        </Box>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/services')} sx={{ mb: 2 }}>
          Back to services
        </Button>
        <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: 'primary.main' }}>
          {isEdit ? 'Edit service' : 'Add service'}
        </Typography>

        <Card>
          <CardContent component="form" onSubmit={handleSubmit} sx={{ p: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Name"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    clearError('name');
                  }}
                  required
                  fullWidth
                  {...fieldProps('name')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Internal charges"
                  value={internalCharges}
                  onChange={(e) => {
                    setInternalCharges(e.target.value);
                    clearError('internal_charges');
                  }}
                  required
                  fullWidth
                  type="number"
                  {...fieldProps('internal_charges')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="External charges"
                  value={externalCharges}
                  onChange={(e) => {
                    setExternalCharges(e.target.value);
                    clearError('external_charges');
                  }}
                  required
                  fullWidth
                  type="number"
                  {...fieldProps('external_charges')}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Description"
                  value={description}
                  onChange={(e) => {
                    setDescription(e.target.value);
                    clearError('description');
                  }}
                  fullWidth
                  multiline
                  minRows={3}
                  {...fieldProps('description')}
                />
              </Grid>
              <Grid item xs={12} sm={12}>
                <Autocomplete
                  options={statusSelect.options}
                  value={selectedStatus}
                  onChange={(_, v) => {
                    setSelectedStatus(v);
                    clearError('status_id');
                  }}
                  onOpen={loadStatuses}
                  loading={statusSelect.loading}
                  getOptionLabel={(opt) => (opt?.label != null ? opt.label : '')}
                  isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Status"
                      required
                      fullWidth
                      {...fieldProps('status_id')}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sx={{ display: 'flex', gap: 2, mt: 1 }}>
                <Button type="submit" variant="contained" disabled={submitting}>
                  {submitting ? <CircularProgress size={22} /> : isEdit ? 'Save changes' : 'Add service'}
                </Button>
                <Button type="button" variant="outlined" onClick={() => navigate('/services')} disabled={submitting}>
                  Cancel
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Container>
    </PageLayout>
  );
};

export default CreateService;
